/**
 * AccuracyMetrics — Renders engine-accuracy per-engine breakdown.
 *
 * Displays prediction accuracy metrics via MetricCard.
 * Shows which engines predict real settlements best.
 */

import React, { useState } from 'react';
import { Activity, Trophy, TrendingUp, AlertCircle, BarChart3, ChevronDown, ChevronUp } from 'lucide-react';
import { cn } from '../../lib/utils';
import { FinancialValue } from '../shared/FinancialValue';
import { MetricCard } from '../shared/MetricCard';
import { StatusBadge } from '../shared/StatusBadge';
import { EngineAccuracyResult, EngineAccuracyMetrics } from '../../hooks/useCopilot';

interface AccuracyMetricsProps {
  result: EngineAccuracyResult | null;
  isLoading: boolean;
}

/**
 * Get accuracy grade based on score
 */
function getAccuracyGrade(accuracy: number): { grade: string; variant: Parameters<typeof StatusBadge>[0]['variant'] } {
  if (accuracy >= 0.9) return { grade: 'Excellent', variant: 'success' };
  if (accuracy >= 0.75) return { grade: 'Good', variant: 'active' };
  if (accuracy >= 0.6) return { grade: 'Fair', variant: 'warning' };
  return { grade: 'Poor', variant: 'error' };
}

/**
 * Format engine name for display
 */
function formatEngineName(engineId: string): string {
  return engineId
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (l) => l.toUpperCase())
    .replace(/Epi/g, 'EPI')
    .replace(/Pv/g, 'PV');
}

/**
 * Sort engines by accuracy (best first)
 */
function sortByAccuracy(engines: EngineAccuracyMetrics[]): EngineAccuracyMetrics[] {
  return [...engines].sort((a, b) => {
    const scoreA = a.accuracy_score || a.closest_prediction_rate;
    const scoreB = b.accuracy_score || b.closest_prediction_rate;
    return scoreB - scoreA;
  });
}

export function AccuracyMetrics({ result, isLoading }: AccuracyMetricsProps) {
  const [expandedEngine, setExpandedEngine] = useState<string | null>(null);
  const [sortBy, setSortBy] = useState<'accuracy' | 'predictions'>('accuracy');

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12" data-testid="accuracy-loading">
        <div className="flex flex-col items-center gap-3">
          <div
            className="w-8 h-8 rounded-full border-2 border-t-transparent animate-spin"
            style={{ borderColor: 'var(--color-brand)', borderTopColor: 'transparent' }}
          />
          <span className="text-sm text-[var(--color-text-muted)]">
            Loading accuracy metrics...
          </span>
        </div>
      </div>
    );
  }

  if (!result) {
    return (
      <div className="py-8 text-center" data-testid="accuracy-empty">
        <div className="text-[var(--color-text-muted)]">
          <Activity className="w-8 h-8 mx-auto mb-2 opacity-50" />
          <p className="text-sm">No accuracy data available</p>
          <p className="text-xs mt-1 max-w-[200px] mx-auto">
            Accuracy metrics require recorded settlement outcomes
          </p>
        </div>
      </div>
    );
  }

  const engines = result.engines || [];
  const totalSettlements = result.overall?.total_settlements || 0;
  const meanAccuracy = result.overall?.mean_accuracy || 0;

  if (engines.length === 0) {
    return (
      <div className="space-y-4" data-testid="accuracy-no-data">
        <div
          className="p-4 rounded-md text-sm"
          style={{
            backgroundColor: 'rgba(255, 102, 0, 0.12)',
            border: '1px solid rgba(255, 102, 0, 0.30)',
            color: 'var(--color-brand)',
          }}
        >
          <div className="flex items-start gap-2">
            <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
            <div>
              <div className="font-medium">No Outcomes Recorded Yet</div>
              <div className="opacity-90 mt-1">
                {result.note || 'Accuracy metrics are computed by comparing predictions to actual settlements. Record some outcomes to see engine accuracy.'}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const sortedEngines = sortByAccuracy(engines);
  const bestEngine = sortedEngines[0];

  return (
    <div className="space-y-4" data-testid="accuracy-metrics">
      {/* Overall Stats */}
      <div className="grid grid-cols-2 gap-3">
        <div className="p-3 rounded-md border border-[var(--color-border)] bg-[var(--color-bg-elevated)]">
          <div className="text-xs text-[var(--color-text-muted)] uppercase tracking-wide">
            Settlements
          </div>
          <div className="mt-1 font-mono text-2xl font-bold text-[var(--color-text-primary)]">
            {totalSettlements.toLocaleString()}
          </div>
        </div>

        <div className="p-3 rounded-md border border-[var(--color-border)] bg-[var(--color-bg-elevated)]">
          <div className="text-xs text-[var(--color-text-muted)] uppercase tracking-wide">
            Mean Accuracy
          </div>
          <div className="mt-1 font-mono text-2xl font-bold"
            style={{
              color: meanAccuracy >= 0.8
                ? 'var(--color-verified)'
                : meanAccuracy >= 0.6
                  ? 'var(--color-brand)'
                  : 'var(--color-text-muted)'
            }}
          >
            {(meanAccuracy * 100).toFixed(0)}%
          </div>
        </div>
      </div>

      {/* Best Engine Highlight */}
      {bestEngine && (
        <div
          className="p-4 rounded-md border"
          style={{
            borderColor: 'var(--color-needs-human-tint-bd)',
            backgroundColor: 'var(--color-needs-human-tint)',
          }}
        >
          <div className="flex items-center gap-2 mb-2">
            <Trophy className="w-4 h-4" style={{ color: 'var(--color-brand)' }} />
            <span className="text-xs font-medium uppercase tracking-wide" style={{ color: 'var(--color-brand)' }}>
              Best Predictor
            </span>
          </div>

          <div className="text-lg font-medium text-[var(--color-text-primary)]">
            {formatEngineName(bestEngine.engine_id)}
          </div>

          <div className="mt-2 grid grid-cols-2 gap-2">
            <div>
              <div className="text-xs text-[var(--color-text-muted)]">Closest Rate</div>
              <div className="font-mono text-sm font-semibold"
                style={{ color: 'var(--color-verified)' }}
              >
                {(bestEngine.closest_prediction_rate * 100).toFixed(1)}%
              </div>
            </div>
            <div>
              <div className="text-xs text-[var(--color-text-muted)]">Predictions</div>
              <div className="font-mono text-sm font-semibold text-[var(--color-text-primary)]">
                {bestEngine.total_predictions}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Sort Toggle */}
      <div className="flex items-center justify-between">
        <span className="text-xs font-medium uppercase tracking-wide text-[var(--color-text-label)]">
          Engine Rankings
        </span>
        <button
          onClick={() => setSortBy(sortBy === 'accuracy' ? 'predictions' : 'accuracy')}
          className="text-xs text-[var(--color-text-muted)] hover:text-[var(--color-brand)] transition-colors"
        >
          Sort by {sortBy === 'accuracy' ? 'Predictions' : 'Accuracy'}
        </button>
      </div>

      {/* Engine List */}
      <div className="space-y-2">
        {(sortBy === 'predictions'
          ? [...engines].sort((a, b) => b.total_predictions - a.total_predictions)
          : sortedEngines
        ).map((engine, index) => {
          const isExpanded = expandedEngine === engine.engine_id;
          const accuracyScore = engine.accuracy_score || engine.closest_prediction_rate;
          const { grade, variant } = getAccuracyGrade(accuracyScore);

          return (
            <div
              key={engine.engine_id}
              className={cn(
                'rounded-md border transition-colors',
                index === 0 && sortBy === 'accuracy'
                  ? 'border-[var(--color-needs-human-tint-bd)] bg-[var(--color-needs-human-tint)]'
                  : 'border-[var(--color-border)] bg-[var(--color-bg-elevated)]'
              )}
              data-testid={`accuracy-engine-${engine.engine_id}`}
            >
              <button
                onClick={() => setExpandedEngine(isExpanded ? null : engine.engine_id)}
                className="w-full p-3 flex items-center justify-between"
              >
                <div className="flex items-center gap-3">
                  <div
                    className="w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold"
                    style={{
                      backgroundColor: index < 3 && sortBy === 'accuracy'
                        ? 'var(--color-brand)'
                        : 'var(--color-bg-hover)',
                      color: index < 3 && sortBy === 'accuracy'
                        ? 'var(--color-bg-base)'
                        : 'var(--color-text-muted)',
                    }}
                  >
                    {index + 1}
                  </div>
                  <div className="text-left">
                    <div className="text-sm font-medium text-[var(--color-text-primary)]">
                      {formatEngineName(engine.engine_id)}
                    </div>
                    <div className="text-xs text-[var(--color-text-muted)]">
                      {engine.total_predictions} predictions
                    </div>
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  <StatusBadge text={grade} variant={variant} size="sm" />
                  {isExpanded ? (
                    <ChevronUp className="w-4 h-4 text-[var(--color-text-muted)]" />
                  ) : (
                    <ChevronDown className="w-4 h-4 text-[var(--color-text-muted)]" />
                  )}
                </div>
              </button>

              {isExpanded && (
                <div className="px-3 pb-3 border-t border-[var(--color-border-sub)] pt-3 space-y-3"
                >
                  {/* Main Metrics */}
                  <div className="grid grid-cols-2 gap-2">
                    <div className="p-2 rounded bg-[var(--color-bg-base)]">
                      <div className="text-xs text-[var(--color-text-muted)]">Closest Rate</div>
                      <div
                        className="font-mono text-lg font-semibold"
                        style={{
                          color: engine.closest_prediction_rate >= 0.5
                            ? 'var(--color-verified)'
                            : 'var(--color-text-muted)'
                        }}
                      >
                        {(engine.closest_prediction_rate * 100).toFixed(1)}%
                      </div>
                    </div>

                    <div className="p-2 rounded bg-[var(--color-bg-base)]">
                      <div className="text-xs text-[var(--color-text-muted)]">Mean Variance</div>
                      <FinancialValue value={engine.mean_absolute_variance} size="lg" />
                    </div>
                  </div>

                  {/* Additional Metrics */}
                  <div className="space-y-2">
                    <div className="flex justify-between items-center">
                      <span className="text-xs text-[var(--color-text-muted)]">
                        Directional Bias
                      </span>
                      <span
                        className={cn(
                          'text-sm font-mono',
                          engine.mean_signed_variance > 0
                            ? 'text-[var(--color-verified)]'
                            : engine.mean_signed_variance < 0
                              ? 'text-[var(--color-text-muted)]'
                              : 'text-[var(--color-text-primary)]'
                        )}
                      >
                        {engine.mean_signed_variance > 0 ? '+' : ''}
                        {engine.mean_signed_variance.toLocaleString('en-US', {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })}
                      </span>
                    </div>

                    <div className="flex justify-between items-center">
                      <span className="text-xs text-[var(--color-text-muted)]">
                        Mean Abs Variance
                      </span>
                      <FinancialValue value={engine.mean_absolute_variance} size="sm" />
                    </div>

                    {engine.accuracy_score !== undefined && (
                      <div className="flex justify-between items-center">
                        <span className="text-xs text-[var(--color-text-muted)]">
                          Accuracy Score
                        </span>
                        <span
                          className="text-sm font-mono"
                          style={{
                            color: engine.accuracy_score >= 0.8
                              ? 'var(--color-verified)'
                              : engine.accuracy_score >= 0.6
                                ? 'var(--color-brand)'
                                : 'var(--color-text-muted)'
                          }}
                        >
                          {(engine.accuracy_score * 100).toFixed(1)}%
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Note */}
      {result.note && (
        <div className="text-xs text-[var(--color-text-muted)] italic">
          {result.note}
        </div>
      )}
    </div>
  );
}

export default AccuracyMetrics;
